import React, { createContext, useContext, useState, useEffect } from 'react';
import { authApi, UserDto, LoginDto, permissionsApi } from '../services/api';

interface AuthContextType {
  isAuthenticated: boolean;
  user: UserDto | null;
  permissions: string[];
  role: string | null;
  login: (credentials: LoginDto) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserDto | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [role, setRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Verificar si hay una sesión activa al cargar
  useEffect(() => {
    const checkAuth = async () => {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      const storedUser = localStorage.getItem('user');
      const storedPermissions = localStorage.getItem('permissions');
      const storedRole = localStorage.getItem('role');

      if (accessToken && storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsAuthenticated(true);
          
          // Load permissions if available
          if (storedPermissions) {
            setPermissions(JSON.parse(storedPermissions));
          }
          if (storedRole) {
            setRole(storedRole);
          }
        } catch (error) {
          console.error('Error parsing user data:', error);
          // Si hay error, intentar refresh
          if (refreshToken) {
            try {
              const response = await authApi.refreshToken(refreshToken);
              localStorage.setItem('accessToken', response.accessToken || '');
              localStorage.setItem('refreshToken', response.refreshToken || '');
              localStorage.setItem('user', JSON.stringify(response.user));
              setUser(response.user);
              setIsAuthenticated(true);
              
              // Fetch permissions after refresh
              try {
                const userPerms = await permissionsApi.getMyPermissions();
                localStorage.setItem('permissions', JSON.stringify(userPerms.permissions));
                localStorage.setItem('role', userPerms.role);
                setPermissions(userPerms.permissions);
                setRole(userPerms.role);
              } catch (permError) {
                console.error('Error fetching permissions:', permError);
              }
            } catch (refreshError) {
              // Si el refresh falla, limpiar todo
              localStorage.clear();
              setIsAuthenticated(false);
              setUser(null);
              setPermissions([]);
              setRole(null);
            }
          }
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (credentials: LoginDto) => {
    try {
      const response = await authApi.login(credentials);
      
      // Guardar tokens y usuario
      localStorage.setItem('accessToken', response.accessToken || '');
      localStorage.setItem('refreshToken', response.refreshToken || '');
      localStorage.setItem('user', JSON.stringify(response.user));
      
      setUser(response.user);
      setIsAuthenticated(true);
      
      // Fetch user permissions (non-blocking)
      // If this fails, the user can still use the app, just without permission-based UI filtering
      setTimeout(async () => {
        try {
          const userPerms = await permissionsApi.getMyPermissions();
          localStorage.setItem('permissions', JSON.stringify(userPerms.permissions));
          localStorage.setItem('role', userPerms.role);
          setPermissions(userPerms.permissions);
          setRole(userPerms.role);
          console.log('✅ Permissions loaded successfully:', userPerms.permissions.length, 'permissions');
        } catch (permError: any) {
          console.warn('⚠️ Could not load permissions. UI will show all elements.', permError.message);
          // Set empty permissions array so the app doesn't break
          setPermissions([]);
          setRole(response.user.role || '');
        }
      }, 100);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authApi.revokeToken(refreshToken);
      }
    } catch (error) {
      console.error('Error revoking token:', error);
    } finally {
      // Limpiar todo
      localStorage.clear();
      setIsAuthenticated(false);
      setUser(null);
      setPermissions([]);
      setRole(null);
    }
  };

  // Permission helper methods
  const hasPermission = (permission: string): boolean => {
    return permissions.includes(permission);
  };

  const hasAnyPermission = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.some(p => permissions.includes(p));
  };

  const hasAllPermissions = (requiredPermissions: string[]): boolean => {
    return requiredPermissions.every(p => permissions.includes(p));
  };

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      user, 
      permissions,
      role,
      login, 
      logout, 
      isLoading,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
